import axiosInstance from "../Interceptors/axiosInstance.jsx";
import Cookies from "js-cookie";
import { fetchUserProfile } from "./newUserService";
import { normalizeProfileResult } from "../utils/profileUtils";
import {
  forceDraftStatusOnFormData,
  isSuperAdminRole,
  preserveStatusOnFormData,
} from "../utils/articleApprovalWorkflow";
import { resolveArticleStatus } from "../components/NewsManagement/Articles/articleStatusUtils";
import {
  normalizeArticleApiItem,
  normalizeArticleListItem,
} from "../components/NewsManagement/Articles/articleFormUtils";

const getMultipartHeaders = () => ({
  "Content-Type": "multipart/form-data",
  Authorization: `Bearer ${Cookies.get("accessToken")}`,
});

const headers = {
  accept: "*/*",
  Authorization: `Bearer ${Cookies.get("accessToken")}`,
  "Content-Type": "application/json",
};

let cachedIsSuperAdmin = null;

// Article approval workflow
const resolveIsSuperAdmin = async () => {
  if (cachedIsSuperAdmin !== null) {
    return cachedIsSuperAdmin;
  }

  const response = await fetchUserProfile();
  const profile = normalizeProfileResult(response.data?.result);
  cachedIsSuperAdmin = isSuperAdminRole(profile?.roleName);
  return cachedIsSuperAdmin;
};

// Article approval workflow
const getArticleIdFromFormData = (formData) =>
  formData.get("id") || formData.get("Id");

export const getArticlesList = async () => {
  const response = await axiosInstance.get("articles/getlist", { headers });
  const result = response.data?.result;

  if (Array.isArray(result)) {
    return {
      ...response,
      data: {
        ...response.data,
        result: result.map((item) => normalizeArticleApiItem(item)),
      },
    };
  }

  return response;
};

export const getArticlesFilterList = async (payload) => {
  const response = await axiosInstance.post(
    "contents/articles-filter",
    payload,
    { headers }
  );
  const items = response.data?.result?.items;

  if (Array.isArray(items)) {
    return {
      ...response,
      data: {
        ...response.data,
        result: {
          ...response.data.result,
          items: items.map((item) => normalizeArticleListItem(item)),
        },
      },
    };
  }

  return response;
};

export const getArticleById = async (id) => {
  const response = await axiosInstance.get(`articles/get/${id}`, { headers });
  return normalizeArticleApiItem(response.data.result);
};

export const addArticle = async (formData) => {
  // Article approval workflow
  const isSuperAdmin = await resolveIsSuperAdmin();
  const payload = isSuperAdmin
    ? formData
    : forceDraftStatusOnFormData(formData);

  return await axiosInstance.post("articles/add", payload, {
    headers: getMultipartHeaders(),
  });
};

export const updateArticle = async (formData) => {
  // Article approval workflow
  const isSuperAdmin = await resolveIsSuperAdmin();
  let payload = formData;

  if (!isSuperAdmin) {
    const articleId = getArticleIdFromFormData(formData);
    if (articleId) {
      const existing = await getArticleById(articleId);
      const existingStatus = resolveArticleStatus(existing);
      payload = preserveStatusOnFormData(formData, existingStatus);
    } else {
      payload = forceDraftStatusOnFormData(formData);
    }
  }

  return await axiosInstance.put("articles/update", payload, {
    headers: getMultipartHeaders(),
  });
};

export const deleteArticle = async (id) => {
  return await axiosInstance.delete(`articles/delete/${id}`, { headers });
};

export const changeArticleStatus = async ({ id, status }) => {
  // Article approval workflow
  const isSuperAdmin = await resolveIsSuperAdmin();
  if (!isSuperAdmin) {
    const error = new Error("Forbidden");
    error.response = {
      status: 403,
      data: {
        isSuccess: false,
        message: "Only Super Admin can publish or unpublish articles.",
      },
    };
    throw error;
  }

  return await axiosInstance.patch(
    "articles/change-status",
    {
      id,
      Id: id,
      status,
      Status: status,
    },
    { headers }
  );
};
